import React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

const WeeklyStats = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [userId] = useState(1); // Hardcode user 1 for now
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [days, userId]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/stats/weekly?userId=${userId}&days=${days}`);
      const data = await response.json();

      if (data.success) {
        setStats(data);
      } else {
        setError(data.error || 'Lỗi khi tải thống kê');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Không thể kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  // Chart data for daily PnL
  const getDailyPnlChart = () => {
    if (!stats || !stats.dailyPnl) return null;

    const labels = stats.dailyPnl.map(d => d.trade_date).reverse();
    const data = stats.dailyPnl.map(d => parseFloat(d.total_pnl)).reverse();

    return {
      labels,
      datasets: [
        {
          label: 'P&L hàng ngày (USDT)',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Biểu đồ P&L theo ngày'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) return null;

  const { overall, dailyPnl, symbolStats, recentTrades, tpSlStats, drawdown } = stats;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          📊 Thống kê giao dịch
        </Typography>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Thời gian</InputLabel>
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            label="Thời gian"
          >
            <MenuItem value={7}>7 ngày</MenuItem>
            <MenuItem value={14}>14 ngày</MenuItem>
            <MenuItem value={30}>30 ngày</MenuItem>
            <MenuItem value={60}>60 ngày</MenuItem>
            <MenuItem value={90}>90 ngày</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overall Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Tổng P&L
              </Typography>
              <Typography variant="h5" component="div" color={overall.total_pnl >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(overall.total_pnl || 0)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {overall.total_pnl >= 0 ? <TrendingUpIcon color="success" /> : <TrendingDownIcon color="error" />}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  Trung bình: {formatCurrency(overall.avg_pnl || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Tỷ lệ thắng
              </Typography>
              <Typography variant="h5" component="div">
                {formatPercent(overall.win_rate)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(overall.win_rate || 0)}
                  color={parseFloat(overall.win_rate || 0) >= 50 ? 'success' : 'warning'}
                />
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {overall.winning_trades || 0} thắng / {overall.losing_trades || 0} thua
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Profit Factor
              </Typography>
              <Typography variant="h5" component="div" color={parseFloat(overall.profit_factor || 0) >= 1 ? 'success.main' : 'error.main'}>
                {parseFloat(overall.profit_factor || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {parseFloat(overall.profit_factor || 0) >= 2 ? '🔥 Xuất sắc' : parseFloat(overall.profit_factor || 0) >= 1 ? '✅ Tốt' : '⚠️ Cần cải thiện'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Tổng số lệnh
              </Typography>
              <Typography variant="h5" component="div">
                {overall.total_trades || 0}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                <Chip label={`Win: ${overall.winning_trades || 0}`} size="small" color="success" />
                <Chip label={`Loss: ${overall.losing_trades || 0}`} size="small" color="error" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Daily PnL Chart */}
      {dailyPnl && dailyPnl.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Line data={getDailyPnlChart()} options={chartOptions} />
          </CardContent>
        </Card>
      )}

      {/* TP/SL Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ShowChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Take Profit Stats
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  Số lần hit TP: <strong>{tpSlStats.tp.count || 0}</strong>
                </Typography>
                <Typography variant="body1" color={parseFloat(tpSlStats.tp.total_pnl || 0) >= 0 ? 'success.main' : 'error.main'}>
                  Tổng P&L từ TP: <strong>{formatCurrency(tpSlStats.tp.total_pnl || 0)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Trung bình/TP: {formatCurrency(tpSlStats.tp.avg_pnl || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingDownIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Stop Loss Stats
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  Số lần hit SL: <strong>{tpSlStats.sl.count || 0}</strong>
                </Typography>
                <Typography variant="body1" color="error.main">
                  Tổng P&L từ SL: <strong>{formatCurrency(tpSlStats.sl.total_pnl || 0)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Trung bình/SL: {formatCurrency(tpSlStats.sl.avg_pnl || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Drawdown Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssessmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Drawdown Analysis
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                Max Drawdown: <strong style={{ color: 'red' }}>{formatCurrency(drawdown.max_drawdown || 0)}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                Avg Drawdown: <strong>{formatCurrency(drawdown.avg_drawdown || 0)}</strong>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top Symbols */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🏆 Top 10 Symbol theo P&L
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Số lệnh</TableCell>
                  <TableCell align="right">Win/Loss</TableCell>
                  <TableCell align="right">Win Rate</TableCell>
                  <TableCell align="right">Total P&L</TableCell>
                  <TableCell align="right">Avg P&L</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {symbolStats && symbolStats.map((row) => (
                  <TableRow key={row.symbol}>
                    <TableCell component="th" scope="row">
                      <strong>{row.symbol}</strong>
                    </TableCell>
                    <TableCell align="right">{row.total_trades}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${row.wins}/${row.losses}`} size="small" />
                    </TableCell>
                    <TableCell align="right">{formatPercent(row.win_rate)}</TableCell>
                    <TableCell align="right" style={{ color: parseFloat(row.total_pnl) >= 0 ? 'green' : 'red' }}>
                      <strong>{formatCurrency(row.total_pnl)}</strong>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.avg_pnl)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Recent Trades */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 20 lệnh gần nhất
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Thời gian</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">P&L</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTrades && recentTrades.map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell>
                      {new Date(trade.created_at).toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell>{trade.symbol}</TableCell>
                    <TableCell>
                      <Chip
                        label={trade.side}
                        size="small"
                        color={trade.side === 'BUY' ? 'success' : trade.side === 'SELL' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">${parseFloat(trade.price).toFixed(6)}</TableCell>
                    <TableCell align="right">{parseFloat(trade.quantity).toFixed(4)}</TableCell>
                    <TableCell align="right" style={{ color: parseFloat(trade.pnl) >= 0 ? 'green' : 'red' }}>
                      <strong>{formatCurrency(trade.pnl)}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip label={trade.status} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WeeklyStats;
